/**
 * API Client - Axios instance with JWT authentication and token refresh
 */

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../../stores/authStore';

// API base URL — always empty in this architecture. The app calls the API
// SAME-ORIGIN (relative `/api/...`), which the nginx (prod) / docker-compose
// nginx (dev) reverse-proxy forwards to the backend, hiding the backend URL from
// the browser. A cross-origin VITE_API_URL is intentionally NOT supported (the
// strict CSP `connect-src 'self'` blocks it). `??` (not `||`) keeps an explicit
// empty string rather than falling back.
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Resolve a server-issued file URL (e.g. an admin `downloadUrl` or a `profilePic`)
 * to an absolute URL against the API origin.
 *
 * The backend issues these signed URLs as relative paths (`/api/v1/files/...`).
 * Because the SPA is served from a different origin than the API, a relative URL
 * would resolve against the web origin (and fall through to the SPA). Prefixing
 * with API_BASE_URL (the same VITE_API_URL the app already uses for the API)
 * points it at the API so the file streams.
 *
 * Idempotent: already-absolute URLs (http/https) are returned unchanged, so this
 * stays correct even if the backend later emits absolute URLs.
 */
export function resolveApiUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
}

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds
});

// Track if token refresh is in progress
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

// Request interceptor - attach JWT token (prefer impersonation token when active)
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const { accessToken, impersonation } = useAuthStore.getState();

    if (config.headers) {
      // When impersonation is active, use the impersonation token for every request,
      // including calls to /impersonation/end (so the server validates via the same session).
      if (impersonation?.token) {
        config.headers.Authorization = `Bearer ${impersonation.token}`;
      } else if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle 401 and refresh token
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
      _backgroundRequest?: boolean;
    };

    // If error is not 401 or request already retried, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // Background requests (e.g. notification polling) should not trigger auth redirect
    if (originalRequest._backgroundRequest) {
      return Promise.reject(error);
    }

    // If token refresh endpoint failed, clear auth and redirect to login
    if (originalRequest.url?.includes('/auth/refresh')) {
      useAuthStore.getState().clearAuth();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // If already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(() => {
          return apiClient(originalRequest);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const { refreshToken, setAccessToken, clearAuth } = useAuthStore.getState();

    if (!refreshToken) {
      clearAuth();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    try {
      // Call refresh token endpoint
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refreshToken,
      });

      const { accessToken: newAccessToken } = response.data;

      // Update access token in store
      setAccessToken(newAccessToken);

      // Update authorization header for original request
      if (originalRequest.headers) {
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      }

      // Process queued requests
      processQueue(null);

      // Retry original request
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Token refresh failed - clear auth and redirect to login
      processQueue(refreshError as AxiosError);
      clearAuth();
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default apiClient;
